import { useTranslation } from 'react-i18next';
import { LabMicrobiologyView } from '@/components/lab/LabMicrobiologyView';
import { LabPartyBlock } from '@/components/lab/LabPartyBlock';
import { LabProvisionalStarLegend } from '@/components/lab/LabProvisionalStar';
import { LabResultRowView } from '@/components/lab/LabResultRow';
import {
  codedCommentMeta,
  compareResultsDateTimeDesc,
  formatLabDateTime,
  laboratoryResultsComments,
  requisitionCommentItems,
  statusLabel,
} from '@/lib/lab/labDisplay';
import {
  currentByResultKey,
  replacementForInvestigation,
  replacementForItem,
  replacementKind,
  reportCardItems,
  reportCardMicro,
} from '@/lib/lab/labReportCard';
import type { LabCodedComment, LabDetail, LabOverview, LabReportRow } from '@/lib/lab/labTypes';
import { formatRevealedCpr } from '@/lib/cprDisplay';

function replacedByMessage(
  kind: ReturnType<typeof replacementKind>,
  when: string,
  t: (key: string, opts?: { when: string }) => string,
): string {
  switch (kind) {
    case 'rettet_svar':
      return t('patients.lab.replaced_by_corrected', { when });
    case 'endeligt_svar':
      return t('patients.lab.replaced_by_final', { when });
    case 'svar':
      return t('patients.lab.replaced_by_result', { when });
    default: {
      const _never: never = kind;
      return _never;
    }
  }
}

function CodedCommentBlock({ title, items }: { title: string; items: LabCodedComment[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-s4">
      <h3>{title}</h3>
      <div className="lab-section mt-s2">
        {items.map((item, index) => {
          const meta = codedCommentMeta(item);
          return (
            <div key={`${item.code ?? ''}-${index}`}>
              {meta ? <p className="text-sm text-ds-muted">{meta}</p> : null}
              <p className="whitespace-pre-wrap">{item.text || '—'}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LabReportDetail({
  report,
  data,
  revealedCpr,
  clinicId,
}: {
  report: LabReportRow;
  data: LabDetail | LabOverview;
  revealedCpr: string | null;
  clinicId: string;
}) {
  const { t } = useTranslation();
  const items = reportCardItems(report.id, data);
  const currentByKey = currentByResultKey(data.current);
  const versionsByResult = new Map<string, typeof data.versions>();
  for (const v of data.versions) {
    const list = versionsByResult.get(v.result_id) ?? [];
    list.push(v);
    versionsByResult.set(v.result_id, list);
  }
  const versionsByInvestigation = new Map<string, typeof data.investigation_versions>();
  for (const v of data.investigation_versions ?? []) {
    const list = versionsByInvestigation.get(v.investigation_id) ?? [];
    list.push(v);
    versionsByInvestigation.set(v.investigation_id, list);
  }
  const micro = reportCardMicro(report.id, data);
  const investigations = micro.investigations
    .slice()
    .sort((a, b) => compareResultsDateTimeDesc(a.results_datetime, b.results_datetime));
  const envelopes = data.envelopes.filter((e) => !e.report_id || e.report_id === report.id);
  const objectLinks = (data.object_links ?? []).filter((l) => l.report_id === report.id);
  const cprLabel = report.patient_id_kind === 'erstatnings_cpr' ? 'Erstatnings-CPR' : 'CPR';

  return (
    <article className="lab-report">
      <div className="flex flex-wrap items-center gap-s2">
        <h3 className="text-ds-text">{report.kind}</h3>
        <span className="lab-chip">{statusLabel(report.report_status_code)}</span>
        {report.result_status_code ? <span className="lab-chip">{statusLabel(report.result_status_code)}</span> : null}
        {report.is_copy ? <span className="lab-chip lab-chip--copy">Kopisvar</span> : null}
        {report.consent_given === false ? (
          <span className="lab-chip lab-chip--warn">{t('patients.lab.consent_refused')}</span>
        ) : null}
      </div>
      {report.consent_given === false ? (
        <p className="mt-s3 text-sm text-ds-muted">{t('patients.lab.consent_refused_block')}</p>
      ) : null}

      <dl className="lab-grid mt-s4">
        <LabPartyBlock title="Afsender" party={report.sender} register={report.sender_register} />
        <LabPartyBlock title="Modtager / rekvirent" party={report.receiver} register={report.receiver_register} />
        {report.cc_receiver ? (
          <LabPartyBlock title="Kopimodtager" party={report.cc_receiver} register={report.cc_receiver_register} />
        ) : null}
        <div className="lab-field">
          <dt>Patient</dt>
          <dd>
            {report.patient_name || '—'}
            <div className="text-sm text-ds-muted">
              {cprLabel}: {revealedCpr ? formatRevealedCpr(revealedCpr) : '••••••-••••'}
            </div>
          </dd>
        </div>
        <div className="lab-field">
          <dt>Rekvisition</dt>
          <dd>
            Læge {report.requester_sample_id || '—'} · Lab {report.lab_internal_sample_id || '—'}
          </dd>
        </div>
        <div className="lab-field">
          <dt>Svartid</dt>
          <dd>{formatLabDateTime(report.results_datetime)}</dd>
        </div>
        <div className="lab-field">
          <dt>Prøvetagning</dt>
          <dd>{formatLabDateTime(report.sampling_datetime)}</dd>
        </div>
        {report.sample_received_datetime ? (
          <div className="lab-field">
            <dt>{t('patients.lab.sample_received')}</dt>
            <dd>{formatLabDateTime(report.sample_received_datetime)}</dd>
          </div>
        ) : null}
        {report.sampling_interval_start || report.sampling_interval_end ? (
          <div className="lab-field">
            <dt>Opsamlingsinterval</dt>
            <dd>
              {formatLabDateTime(report.sampling_interval_start)} – {formatLabDateTime(report.sampling_interval_end)}
            </dd>
          </div>
        ) : null}
        <div className="lab-field">
          <dt>Kuvert / brev</dt>
          <dd>
            {envelopes.map((e) => e.envelope_identifier).join(', ') || report.latest_envelope_identifier || '—'}
            {' · '}
            {report.letter_identifier || '—'}
          </dd>
        </div>
      </dl>

      <CodedCommentBlock title="Rekvisitionskommentar" items={requisitionCommentItems(report)} />
      <CodedCommentBlock title="Svarskommentar" items={laboratoryResultsComments(report)} />
      {report.summary ? (
        <div className="mt-s4">
          <h3>{t('patients.lab.report_summary')}</h3>
          <p className="whitespace-pre-wrap">{report.summary}</p>
        </div>
      ) : null}
      {report.clinical_information ? (
        <div className="mt-s4">
          <h3>Klinisk information</h3>
          <p>{report.clinical_information}</p>
        </div>
      ) : null}

      {items.length > 0 ? (
        <div className="mt-s4">
          <h3>Analyser</h3>
          <table className="lab-table">
            <thead>
              <tr>
                <th>Analyse</th>
                <th>Resultat</th>
                <th>Reference</th>
                <th>Producent</th>
                <th>Tidligere</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const current = currentByKey.get(row.result_key);
                const mark = replacementForItem(row, data);
                const superseded = Boolean(mark);
                const replacedBy = !mark
                  ? null
                  : mark.hidden
                    ? t('patients.lab.replaced')
                    : replacedByMessage(
                      mark.kind,
                      formatLabDateTime(mark.results_datetime),
                      t,
                    );
                return (
                  <LabResultRowView
                    key={row.id}
                    row={row}
                    versions={superseded || !current ? undefined : versionsByResult.get(current.id)}
                    clinicId={clinicId}
                    objectLinks={objectLinks}
                    consentGiven={report.consent_given}
                    replacedBy={replacedBy}
                  />
                );
              })}
            </tbody>
          </table>
          <LabProvisionalStarLegend codes={items.map((row) => row.result_status_code)} />
        </div>
      ) : null}

      {investigations.map((inv) => {
        const mark = replacementForInvestigation(inv, data);
        const replacedBy = !mark
          ? null
          : mark.hidden
            ? t('patients.lab.replaced')
            : replacedByMessage(mark.kind, formatLabDateTime(mark.results_datetime), t);
        return (
          <div key={inv.id} className="mt-s4">
            <LabMicrobiologyView
              investigation={inv}
              microscopy={micro.microscopy.find((m) => m.investigation_id === inv.id)}
              organisms={micro.organisms.filter((o) => o.investigation_id === inv.id)}
              antibiotics={micro.antibiotics.filter((a) => a.investigation_id === inv.id)}
              resistance={micro.resistance.filter((r) => r.investigation_id === inv.id)}
              versions={versionsByInvestigation.get(inv.id)}
              clinicId={clinicId}
              objectLinks={objectLinks}
              consentGiven={report.consent_given}
              replacedBy={replacedBy}
            />
          </div>
        );
      })}
    </article>
  );
}
